/**
 * Gravação de VÍDEO curto no navegador (PWA), com teto de duração E de bytes.
 *
 * **Por que gravar no app em vez de aceitar da galeria.** Medido no navegador
 * (06/08/2026), 5 minutos de vídeo:
 *
 *   | origem                          | tamanho  |
 *   |---------------------------------|----------|
 *   | galeria, 4K@30 (celular novo)   | ~1.690 MB|
 *   | galeria, 1080p@30               |   ~640 MB|
 *   | gravado aqui, 480p, cena calma  |     ~3 MB|
 *   | gravado aqui, 480p, muito movimento | ~15 MB|
 *
 * A diferença não está no vídeo — está em QUEM escolhe a qualidade. Da galeria,
 * quem escolheu foi a câmera do parceiro. Aqui, o app decide antes do primeiro
 * quadro existir. Reduzir depois exigiria transcodificar: no servidor é
 * inviável (a VM tem 2 CPUs compartilhados por 16 containers) e no navegador
 * pior ainda (`ffmpeg.wasm` sem `crossOriginIsolated` roda em uma thread só —
 * medido `false` no site — o que daria dezenas de minutos por vídeo).
 *
 * **O teto de BYTES é o que torna isto seguro**, e não é redundante com o de
 * duração. Medição: o mesmo pedido de 800 kbps produziu 2,9 MB numa cena calma
 * e 70 MB em ruído extremo — variação de 24×. `videoBitsPerSecond` é uma
 * SUGESTÃO ao encoder, não um teto: quanto mais movimento, mais ele estoura.
 * Sem medir os bytes durante a gravação, o caso agitado viraria um upload que
 * a VM não aguenta.
 *
 * Para isso o `MediaRecorder` é iniciado com `timeslice` — sem ele os dados só
 * chegam no `stop()`, e aí não haveria como cortar no meio.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export type VideoRecorderStatus =
  | "idle"
  | "requesting" // getUserMedia em voo
  | "recording"
  | "stopped" // tem resultado, aguardando enviar/descartar
  | "denied" // permissão negada
  | "no-device" // sem câmera / não suportado
  | "error";

export interface VideoRecording {
  blob: Blob;
  mimeType: string;
  durationSec: number;
  sizeBytes: number;
  /** Quadro capturado durante a gravação, para servir de miniatura.
   *
   *  Existe porque `preload="metadata"` NÃO impede o navegador de baixar o
   *  vídeo (medido: `readyState 4` ao abrir a conversa). Com miniatura, a bolha
   *  mostra algo reconhecível sem tocar no arquivo de vídeo.
   *
   *  Capturado no cliente de propósito: o upload roda no container `web`, que
   *  deliberadamente NÃO tem ffmpeg (são ~100MB, instalados só nos workers).
   *  `null` se a captura falhar — miniatura é um extra, não pode custar a
   *  gravação. */
  poster: Blob | null;
  /** object URL p/ o preview — revogado em reset/cancel/unmount. */
  url: string;
}

/** Motivo pelo qual a gravação parou sozinha. */
export type MotivoDeCorte = "duracao" | "tamanho";

export interface UseVideoRecorderOptions {
  /** Teto de duração; ao atingir, para sozinho. Default 300s (5 min). */
  maxDurationSec?: number;
  /** Teto de bytes; ao atingir, para sozinho. Default 40 MB.
   *
   *  40 MB porque o upload lê o arquivo inteiro na memória (`file.read()` no
   *  endpoint) e a VM tem 7,8 GB para 16 containers — é a mesma ordem de
   *  grandeza do teto de imagem (25 MB), com folga para o caso agitado. */
  maxBytes?: number;
  /** Largura alvo. 480p é legível para documento e cabe em ~3 MB/5min. */
  targetWidth?: number;
  /** Disparado quando um dos tetos força o corte — o componente avisa o usuário.
   *  Sem isto o vídeo pararia sozinho e o parceiro acharia que travou. */
  onCorteAutomatico?: (motivo: MotivoDeCorte) => void;
}

export interface UseVideoRecorderResult {
  status: VideoRecorderStatus;
  durationSec: number;
  /** Bytes acumulados até agora — o componente mostra ao usuário enquanto grava. */
  bytesAtuais: number;
  error: string | null;
  recording: VideoRecording | null;
  isSupported: boolean;
  /** Stream ao vivo p/ o componente exibir o que a câmera está vendo. */
  streamRef: React.MutableRefObject<MediaStream | null>;
  /** O componente aponta este ref para o <video> do preview ao vivo — é dele
   *  que o quadro da miniatura é desenhado. */
  videoParaPosterRef: React.MutableRefObject<HTMLVideoElement | null>;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
  reset: () => void;
}

/** mp4/avc1 primeiro: é o que o iOS grava e o que TODOS reproduzem. webm é o
 *  fallback do Android/Chrome. Ordem importa — um `.webm` enviado a um iPhone
 *  não toca, e o parceiro não tem como saber por quê. */
const MIMES_PREFERIDOS = [
  "video/mp4;codecs=avc1.42E01E",
  "video/mp4",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm",
];

/** Intervalo dos pedaços. 1s dá granularidade suficiente para cortar por
 *  tamanho sem estourar muito além do teto, e não pesa. */
const INTERVALO_PEDACO_MS = 1000;

/** Sugestão de bitrate ao encoder. Ver o docstring: é sugestão, não teto —
 *  quem garante o tamanho é `maxBytes`. */
const BITRATE_SUGERIDO = 800_000;

export function escolherMimeType(): string {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  for (const t of MIMES_PREFERIDOS) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

function detectarSuporte(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined" &&
    escolherMimeType() !== ""
  );
}

export function useVideoRecorder(options: UseVideoRecorderOptions = {}): UseVideoRecorderResult {
  const {
    maxDurationSec = 300,
    maxBytes = 40 * 1024 * 1024,
    targetWidth = 854,
    onCorteAutomatico,
  } = options;

  const [status, setStatus] = useState<VideoRecorderStatus>("idle");
  const [durationSec, setDurationSec] = useState(0);
  const [bytesAtuais, setBytesAtuais] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState<VideoRecording | null>(null);
  const [isSupported] = useState(detectarSuporte);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pedacosRef = useRef<Blob[]>([]);
  const bytesRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inicioRef = useRef(0);
  const canceladoRef = useRef(false);
  const urlRef = useRef<string | null>(null);
  /** Quadro capturado ~1s após o início — tempo para a câmera expor e focar.
   *  No instante zero a imagem costuma sair escura ou borrada. */
  const posterRef = useRef<Blob | null>(null);
  const videoParaPosterRef = useRef<HTMLVideoElement | null>(null);

  // Refs para os callbacks/limites: o `ondataavailable` é criado uma vez e
  // precisa enxergar o valor ATUAL, não o do render em que foi criado.
  const onCorteRef = useRef(onCorteAutomatico);
  const maxBytesRef = useRef(maxBytes);
  const maxDurRef = useRef(maxDurationSec);
  useEffect(() => {
    onCorteRef.current = onCorteAutomatico;
    maxBytesRef.current = maxBytes;
    maxDurRef.current = maxDurationSec;
  }, [onCorteAutomatico, maxBytes, maxDurationSec]);

  const limparTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const pararStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const revogarPreview = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  }, []);

  const pararGravador = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state !== "inactive") r.stop();
  }, []);

  const start = useCallback(async () => {
    if (!isSupported) {
      setStatus("no-device");
      setError("Este aparelho ou navegador não grava vídeo.");
      return;
    }
    setError(null);
    setRecording(null);
    revogarPreview();
    pedacosRef.current = [];
    bytesRef.current = 0;
    setBytesAtuais(0);
    setDurationSec(0);
    canceladoRef.current = false;
    setStatus("requesting");

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // `ideal`, não `exact`: com `exact` um aparelho que não faça exatamente
        // 854x480 falharia a captura inteira. `ideal` negocia o mais próximo.
        video: {
          width: { ideal: targetWidth },
          height: { ideal: Math.round((targetWidth * 9) / 16) },
          frameRate: { ideal: 24, max: 30 },
          facingMode: { ideal: "environment" }, // câmera traseira: é documento
        },
        audio: true,
      });
    } catch (e) {
      const nome = (e as DOMException)?.name;
      if (nome === "NotAllowedError" || nome === "SecurityError") {
        setStatus("denied");
        setError("Permissão de câmera negada. Autorize nas configurações do navegador.");
      } else if (nome === "NotFoundError" || nome === "OverconstrainedError") {
        setStatus("no-device");
        setError("Nenhuma câmera encontrada neste aparelho.");
      } else {
        setStatus("error");
        setError("Não foi possível acessar a câmera.");
      }
      return;
    }

    // Se cancel()/unmount rodou durante o await, não deixa a câmera ligada.
    if (canceladoRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    streamRef.current = stream;
    const mimeType = escolherMimeType();
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: BITRATE_SUGERIDO });
    } catch {
      pararStream();
      setStatus("error");
      setError("Este navegador não suporta o formato de gravação necessário.");
      return;
    }
    recorderRef.current = rec;

    rec.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      pedacosRef.current.push(e.data);
      bytesRef.current += e.data.size;
      setBytesAtuais(bytesRef.current);
      // TETO DE BYTES — o que protege do caso "cena agitada". Ver docstring:
      // o encoder pode entregar 24× mais que o bitrate pedido.
      if (bytesRef.current >= maxBytesRef.current) {
        onCorteRef.current?.("tamanho");
        pararGravador();
      }
    };

    rec.onstop = () => {
      limparTimer();
      pararStream();
      if (canceladoRef.current) {
        pedacosRef.current = [];
        bytesRef.current = 0;
        setStatus("idle");
        return;
      }
      const blob = new Blob(pedacosRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setRecording({
        blob,
        mimeType,
        durationSec: Math.round((Date.now() - inicioRef.current) / 1000),
        sizeBytes: blob.size,
        url,
        poster: posterRef.current,
      });
      setStatus("stopped");
    };

    // Miniatura: desenha um quadro do stream ao vivo num canvas. Roda depois de
    // ~1,2s para a câmera terminar de expor — no instante zero sai escuro.
    // Toda a captura é best-effort: se falhar, o vídeo segue sem miniatura.
    posterRef.current = null;
    setTimeout(() => {
      try {
        const v = videoParaPosterRef.current;
        if (!v || !v.videoWidth) return;
        const cv = document.createElement("canvas");
        // Miniatura pequena: ela existe para RECONHECER, não para assistir.
        const escala = Math.min(1, 480 / v.videoWidth);
        cv.width = Math.round(v.videoWidth * escala);
        cv.height = Math.round(v.videoHeight * escala);
        cv.getContext("2d")?.drawImage(v, 0, 0, cv.width, cv.height);
        cv.toBlob((b) => (posterRef.current = b), "image/jpeg", 0.7);
      } catch {
        /* sem miniatura; a gravação não é afetada */
      }
    }, 1200);

    inicioRef.current = Date.now();
    // `timeslice`: sem ele os dados só chegariam no stop() e o teto de bytes
    // não teria como agir DURANTE a gravação — que é o ponto dele.
    rec.start(INTERVALO_PEDACO_MS);
    setStatus("recording");

    timerRef.current = setInterval(() => {
      const s = Math.round((Date.now() - inicioRef.current) / 1000);
      setDurationSec(s);
      if (s >= maxDurRef.current) {
        onCorteRef.current?.("duracao");
        pararGravador();
      }
    }, 500);
  }, [isSupported, targetWidth, revogarPreview, pararStream, pararGravador, limparTimer]);

  const stop = useCallback(() => {
    canceladoRef.current = false;
    pararGravador();
  }, [pararGravador]);

  const cancel = useCallback(() => {
    canceladoRef.current = true;
    limparTimer();
    pararGravador();
    pararStream();
    revogarPreview();
    pedacosRef.current = [];
    bytesRef.current = 0;
    setBytesAtuais(0);
    setDurationSec(0);
    setRecording(null);
    setStatus("idle");
  }, [limparTimer, pararGravador, pararStream, revogarPreview]);

  const reset = useCallback(() => {
    revogarPreview();
    pedacosRef.current = [];
    bytesRef.current = 0;
    setBytesAtuais(0);
    setDurationSec(0);
    setRecording(null);
    setError(null);
    setStatus("idle");
  }, [revogarPreview]);

  // Desmontar com a câmera ligada deixaria a luz do aparelho acesa — o usuário
  // acha que está sendo gravado.
  useEffect(() => {
    return () => {
      canceladoRef.current = true;
      limparTimer();
      const r = recorderRef.current;
      if (r && r.state !== "inactive") r.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [limparTimer]);

  return {
    status,
    durationSec,
    bytesAtuais,
    error,
    recording,
    isSupported,
    streamRef,
    videoParaPosterRef,
    start,
    stop,
    cancel,
    reset,
  };
}
